import { fetchReviews } from '@theme/review-api';

/**
 * Custom element that displays a list of reviews with pagination
 */
class ReviewList extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;
  #currentPage = 1;

  connectedCallback() {
    const productId = this.dataset.productId;
    if (productId) {
      this.#currentPage = 1;
      this.loadReviews(productId, this.#currentPage);
    }

    // Handle pagination clicks
    this.addEventListener('click', this.#handlePaginationClick);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.removeEventListener('click', this.#handlePaginationClick);
  }

  /**
   * Handles pagination button clicks
   * @param {Event} event
   */
  #handlePaginationClick = (event) => {
    const button = event.target.closest('[data-page]');
    if (!button || button.disabled) return;

    const page = parseInt(button.dataset.page, 10);
    const productId = this.dataset.productId;
    if (productId && page) {
      this.#currentPage = page;
      this.loadReviews(productId, page);
    }
  };

  /**
   * Loads and renders reviews
   * @param {string} productId
   * @param {number} page
   */
  async loadReviews(productId, page = 1) {
    if (!productId || productId === 'PREVIEW-ID') return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const loadingIndicator = this.querySelector('[ref="loadingIndicator"]');
    const reviewsContent = this.querySelector('[ref="reviewsContent"]');
    const reviewsList = this.querySelector('[ref="reviewsList"]');
    const paginationContainer = this.querySelector('[ref="paginationContainer"]');

    if (loadingIndicator) {
      loadingIndicator.classList.remove('visually-hidden');
    }
    if (reviewsContent) {
      reviewsContent.classList.add('visually-hidden');
    }

    try {
      const reviewsPerPage = parseInt(this.dataset.reviewsPerPage || '10', 10);
      const response = await fetchReviews({
        productId,
        page,
        limit: reviewsPerPage,
      });

      if (this.#abortController.signal.aborted) return;

      if (loadingIndicator) {
        loadingIndicator.classList.add('visually-hidden');
      }

      if (!response || !response.reviews || response.reviews.length === 0) {
        this.classList.add('visually-hidden');
        return;
      }

      this.classList.remove('visually-hidden');
      this.renderReviews(response);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading reviews:', error);
      }
      if (loadingIndicator) {
        loadingIndicator.classList.add('visually-hidden');
      }
    }
  }

  /**
   * Renders the reviews list
   * @param {Object} response
   * @param {Array} response.reviews
   * @param {Object} response.pagination
   */
  renderReviews(response) {
    const { reviews, pagination } = response;
    const reviewsList = this.querySelector('[ref="reviewsList"]');
    const reviewsContent = this.querySelector('[ref="reviewsContent"]');
    const paginationContainer = this.querySelector('[ref="paginationContainer"]');

    if (!reviewsList) return;

    // Render reviews
    let reviewsHTML = '';
    reviews.forEach((review) => {
      const date = new Date(review.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      // Render stars
      let starsHTML = '';
      for (let i = 1; i <= 5; i++) {
        const isFilled = i <= review.rating;
        starsHTML += `
          <svg class="review-item-star ${!isFilled ? 'empty' : ''}" viewBox="0 0 32 32" role="presentation">
            <path d="M31.547 12a.848.848 0 00-.677-.577l-9.427-1.376-4.224-8.532a.847.847 0 00-1.516 0l-4.218 8.534-9.427 1.355a.847.847 0 00-.467 1.467l6.823 6.664-1.612 9.375a.847.847 0 001.23.893l8.428-4.434 8.432 4.432a.847.847 0 001.229-.894l-1.615-9.373 6.822-6.665a.845.845 0 00.214-.869z" />
          </svg>
        `;
      }

      reviewsHTML += `
        <div class="review-item">
          <div class="review-item-header">
            <div class="review-item-meta">
              <div class="review-item-name">${review.customer_name || 'Anonymous'}</div>
              <div class="review-item-date">${date}</div>
              ${review.verified_purchase ? '<span class="review-item-verified">✓ Verified Purchase</span>' : ''}
            </div>
            <div class="review-item-rating">
              <div class="review-item-stars" aria-label="${review.rating} out of 5 stars">
                ${starsHTML}
              </div>
            </div>
          </div>
          ${review.review_text ? `<div class="review-item-text">${this.escapeHtml(review.review_text)}</div>` : ''}
        </div>
      `;
    });

    reviewsList.innerHTML = reviewsHTML;

    // Render pagination
    if (paginationContainer && pagination.totalPages > 1) {
      let paginationHTML = '';

      // Previous button
      paginationHTML += `
        <button
          class="review-pagination-button"
          data-page="${pagination.page - 1}"
          ${pagination.page === 1 ? 'disabled' : ''}
        >
          Previous
        </button>
      `;

      // Page info
      paginationHTML += `
        <div class="review-pagination-info">
          Page ${pagination.page} of ${pagination.totalPages}
        </div>
      `;

      // Next button
      paginationHTML += `
        <button
          class="review-pagination-button"
          data-page="${pagination.page + 1}"
          ${pagination.page >= pagination.totalPages ? 'disabled' : ''}
        >
          Next
        </button>
      `;

      paginationContainer.innerHTML = paginationHTML;
      paginationContainer.classList.remove('visually-hidden');
    } else if (paginationContainer) {
      paginationContainer.classList.add('visually-hidden');
    }

    if (reviewsContent) {
      reviewsContent.classList.remove('visually-hidden');
    }
  }

  /**
   * Escapes HTML to prevent XSS
   * @param {string} text
   * @returns {string}
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

if (!customElements.get('review-list')) {
  customElements.define('review-list', ReviewList);
}
